import { AlertTriangle } from 'lucide-react';

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

import { masterValuesFor, selectClass } from './MappingEditor';

import type { ConnectorTestResult, UnmatchedValues } from '@repo/shared-types';

const pick = (data: Record<string, unknown>, path: string): string => {
  let node: unknown = data;
  for (const key of path.split('.')) {
    node =
      typeof node === 'object' && node !== null ? (node as Record<string, unknown>)[key] : null;
  }
  return node === null || node === undefined ? '' : String(node);
};

const COLUMNS: Array<[string, string]> = [
  ['Make', 'vehicle.manufacturer'],
  ['Model', 'vehicle.model'],
  ['Year', 'vehicle.year'],
  ['Price', 'prices.advertised.amount'],
  ['Mileage', 'vehicle.mileage'],
  ['Fuel', 'vehicle.fuel_type'],
];

/** Values that matched none of our fixed values, each with a "translate to" dropdown. */
const UnmatchedPanel = ({
  unmatched,
  onTranslate,
}: {
  unmatched: UnmatchedValues;
  onTranslate?: (field: string, value: string, to: string) => void;
}) => {
  const rows = Object.entries(unmatched).flatMap(([field, values]) =>
    Object.entries(values).map(([value, count]) => ({ field, value, count })),
  );
  if (rows.length === 0) return null;
  return (
    <div className="space-y-2 rounded-md border border-amber-300 p-3 dark:border-amber-700">
      <p className="font-medium">
        These values didn&apos;t match our fixed values. Pick what each should become (or leave it
        as is):
      </p>
      {rows.map(({ field, value, count }) => (
        <div key={`${field}:${value}`} className="flex flex-wrap items-center gap-2">
          <span className="text-muted-foreground w-44 font-mono text-xs">{field}</span>
          <span className="font-medium">{value}</span>
          <span className="text-muted-foreground text-xs">×{count}</span>
          <span className="text-muted-foreground">→</span>
          <select
            aria-label={`Translate ${value} to`}
            className={selectClass}
            defaultValue=""
            disabled={!onTranslate}
            onChange={(e) => e.target.value && onTranslate?.(field, value, e.target.value)}
          >
            <option value="">choose…</option>
            <option value="__empty__">(leave empty)</option>
            {(masterValuesFor(field) ?? []).map((v) => (
              <option key={v} value={v}>
                {v}
              </option>
            ))}
          </select>
        </div>
      ))}
    </div>
  );
};

/** One fetched page: request summary, page-size warning, raw record and mapped result. */
export const TestPreview = ({
  result,
  onTranslate,
}: {
  result: ConnectorTestResult;
  /** Adds "value → to" to that field's value map ('__empty__' = leave empty). */
  onTranslate?: (field: string, value: string, to: string) => void;
}) => (
  <div className="space-y-3 text-sm">
    <UnmatchedPanel unmatched={result.unmatched ?? {}} onTranslate={onTranslate} />
    <p className="text-muted-foreground font-mono text-xs break-all">{result.request_url}</p>
    <p>
      HTTP {result.status} · returned <strong>{result.returned}</strong>
      {result.total !== null && (
        <>
          {' '}
          of <strong>{result.total.toLocaleString('en-GB')}</strong> total
        </>
      )}
    </p>
    {result.short_page_warning && (
      <div className="flex gap-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-amber-900 dark:border-amber-700 dark:bg-amber-950 dark:text-amber-200">
        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
        <span>{result.short_page_warning}</span>
      </div>
    )}
    <div className="grid gap-3 lg:grid-cols-2">
      <div>
        <p className="mb-1 font-medium">First record (raw)</p>
        <pre className="bg-muted max-h-72 overflow-auto rounded-md p-2 text-xs">
          {JSON.stringify(result.sample_raw[0] ?? null, null, 2)}
        </pre>
      </div>
      <div className="overflow-auto">
        <p className="mb-1 font-medium">Mapped (first {result.sample_mapped.length})</p>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>ID</TableHead>
              {COLUMNS.map(([label]) => (
                <TableHead key={label}>{label}</TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {result.sample_mapped.map((row, i) => (
              <TableRow key={i}>
                <TableCell className="font-mono text-xs">
                  {row.external_id ?? <span className="text-destructive">skipped: {row.skip}</span>}
                </TableCell>
                {COLUMNS.map(([label, path]) => (
                  <TableCell key={label}>{pick(row.data, path)}</TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  </div>
);
